/**
 * Phase 1 (UI overhaul spine) — the ergonomic projection hook.
 *
 * `useView(resource)` is the one call a projection-aware widget makes. It wraps
 * `useViewProjection` and hands back everything a surface needs to render
 * itself at the viewer's breadth/depth:
 *
 *   const v = useView(programRef(programId));
 *   if (v.viewMode === 'no_view') return <NoViewSlot … />;
 *   return (
 *     <SummaryView … />                       // full vs summary card
 *     {v.fieldMasked('revenue') ? <Restricted/> : <Revenue/>}
 *     {v.can('update') && <EditButton/>}
 *   );
 *
 * It adds two things over the raw PolicyResult:
 *   - `can(action)` — allow AND not in actionBlocked, for ANY action, off the
 *     single resolve (the resolver returns the whole actionBlocked set).
 *   - `fieldMasked(field)` — the same snake_case-tolerant redaction predicate
 *     <MaskedField> renders on (shared via types.fieldIsMasked).
 *
 * Note on vocabulary: the spec calls the reduced mode "summary"; the resolver
 * and these types emit "simplified". They are the same mode — we keep
 * "simplified" as the canonical wire value rather than rename across the stack.
 *
 * Fail-closed: while the projection is loading or unresolved, viewMode is
 * "no_view", `can()` is false, and `fieldMasked()` is true for every field.
 */ import { useMemo } from 'react';
import { fieldIsMasked } from './types';
import { useViewProjection } from './useViewProjection';
export function useView(resource, { action = 'read', enabled = true } = {}) {
    const { data, isLoading } = useViewProjection(resource, {
        action,
        enabled
    });
    return useMemo(()=>{
        if (!data) {
            // Fail-closed: nothing visible, nothing permitted, everything masked.
            return {
                viewMode: 'no_view',
                maskedFields: [],
                isLoading,
                can: ()=>false,
                fieldMasked: ()=>true,
                granted: ()=>false
            };
        }
        return {
            viewMode: data.viewMode,
            maskedFields: data.maskedFields,
            isLoading,
            can: (a)=>data.decision === 'allow' && !data.actionBlocked.includes(a),
            fieldMasked: (field)=>fieldIsMasked(data.viewMode, data.maskedFields, field),
            granted: (a)=>data.decision === 'allow' && (data.grantedActions ?? []).includes(a),
            policy: data
        };
    }, [
        data,
        isLoading
    ]);
}
